import { z } from "zod";
import type {
  Archive,
  MhCharacter,
  QuickChatConversation,
  QuickChatMessage,
} from "../../domain/types";
import { affinityTowardsPlayer } from "../../domain/actors";
import { callLLMWithRoutes } from "../../services/llm";
import {
  resolveFeatureRoutes,
  type FeatureRoutingSettings,
} from "../../services/featureRoutes";
import {
  MH_IMPORTED_PRESET_ADAPTATION,
  resolvePresetContent,
} from "../../services/presetAssembler";

export interface QuickChatPreset {
  id: string;
  name: string;
  systemPrompt: string;
  groupSystemPrompt: string;
  assistantPrefill?: string;
  assistantPrefillRole?: "assistant" | "user";
}

const directSchema = z.object({
  narrate: z.string().default(""),
  speak: z.string().default(""),
  triggerCombat: z.boolean().optional(),
});
const groupSchema = directSchema.extend({
  speakerId: z.string().nullable(),
  waitForPlayer: z.boolean().default(false),
});
let presetCache: Promise<QuickChatPreset[]> | null = null;

export function loadQuickChatPresets() {
  presetCache ??= fetch(
    "/presets/mortal-approved/quick-chat/思维链NPC对话.json",
  ).then(async (response) => {
    if (!response.ok) throw new Error("无法载入现场快聊预设");
    return (await response.json()) as QuickChatPreset[];
  });
  return presetCache;
}

export function onsiteCharacters(archive: Archive) {
  return Object.values(archive.characters).filter(
    (character) =>
      character.presence.status === "present" &&
      character.presence.location === archive.scene.location,
  );
}

function characterProfile(character: MhCharacter) {
  return [
    `ID: ${character.id}`,
    `姓名: ${character.identity.name}`,
    `身份: ${character.identity.occupation || character.identity.title || "未知"}`,
    `所属: ${character.identity.affiliation || "无"}`,
    `性格与背景: ${character.bio.background || character.bio.biography || "暂无"}`,
    `当前状态: ${character.status.current}`,
    `当前动机: ${character.bio.currentMotive || "未记录"}`,
    `对玩家好感: ${affinityTowardsPlayer(character)}`,
  ].join("\n");
}

function conversationId(kind: "direct" | "group", ids: string[]) {
  return `quick:${kind}:${[...ids].sort().join("+")}`;
}

function renderHistory(conversation?: QuickChatConversation) {
  return (
    conversation?.messages
      .slice(-20)
      .map((message) => `${message.speakerId}: ${message.content}`)
      .join("\n") || "无"
  );
}

function parseJsonObject(raw: string) {
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i)?.[1];
  const source = fenced ?? raw.slice(raw.indexOf("{"), raw.lastIndexOf("}") + 1);
  return JSON.parse(source);
}

function visibleReply(narrate: string, speak: string) {
  return [narrate.trim(), speak.trim() ? `“${speak.trim()}”` : ""]
    .filter(Boolean)
    .join("\n");
}

function appendMessage(
  conversation: QuickChatConversation,
  message: QuickChatMessage,
) {
  conversation.messages.push(message);
  conversation.updatedAt = message.createdAt;
}

export async function sendQuickChat(options: {
  archive: Archive;
  participantIds: string[];
  input: string;
  settings: FeatureRoutingSettings;
  presetId?: string;
  autoContinueLimit?: number;
  signal?: AbortSignal;
}) {
  const input = options.input.trim();
  if (!input) throw new Error("请输入现场对话内容");
  const available = new Map(
    onsiteCharacters(options.archive).map((character) => [character.id, character]),
  );
  const ids = [...new Set(options.participantIds)];
  const participants = ids.map((id) => available.get(id)).filter(Boolean) as MhCharacter[];
  if (participants.length !== ids.length)
    throw new Error("快聊只允许选择当前场景内的在场人物");
  const kind = ids.length === 1 ? "direct" : "group";
  if (kind === "group" && ids.length < 2) throw new Error("现场群聊至少需要两名 NPC");
  const presets = await loadQuickChatPresets();
  const preset = presets.find((item) => item.id === options.presetId) ?? presets[0];
  if (!preset) throw new Error("没有可用的快聊预设");
  const next = structuredClone(options.archive);
  const id = conversationId(kind, ids);
  const conversation: QuickChatConversation = next.quickChat.conversations[id] ?? {
    id,
    kind,
    participantIds: ids,
    messages: [],
    updatedAt: Date.now(),
  };
  const now = Date.now();
  appendMessage(conversation, {
    id: crypto.randomUUID(),
    role: "user",
    content: input,
    createdAt: now,
    speakerId: "B1",
  });

  const maxReplies = kind === "group" ? Math.max(1, Math.min(6, 1 + (options.autoContinueLimit ?? 0))) : 1;
  let lastMessage = input;
  let lastSpeaker = "B1";
  for (let index = 0; index < maxReplies; index += 1) {
    const vars: Record<string, string> = {
      player_name: next.player.identity.name,
      player_realm: `HR ${next.player.hunter.hr}`,
      current_time: `第${next.gameTime.day}日 ${next.gameTime.hour}:00`,
      current_location: next.scene.location,
      current_story: next.messages.at(-1)?.content ?? "无",
      world_factors: next.worldEvents.join("\n") || "无",
      world_guidance_rules: "遵守怪物猎人正史、公会规则、角色信息边界与玩家行动权。",
      current_vectors: "无",
      last_message: lastMessage,
      chat_history: renderHistory(conversation),
      group_chat_history: renderHistory(conversation),
      group_participants: participants.map(characterProfile).join("\n\n"),
      eligible_group_speakers: participants
        .filter((character) => character.id !== lastSpeaker)
        .map((character) => `- ${character.id} | ${character.identity.name}`)
        .join("\n") || "无",
      blocked_group_speakers:
        lastSpeaker === "B1" ? "无" : `- ${lastSpeaker}：上一条已发言，不可连续接话`,
      last_group_speaker: lastSpeaker,
      group_turn_origin: index === 0 ? "player：玩家手动发言触发" : "auto_npc：NPC 自动续聊触发",
      addressed_group_speaker: "按玩家原话判断",
      group_auto_continue_guidance: "若上一条已把选择交还玩家，必须 waitForPlayer=true。",
      recent_group_actions: conversation.messages.slice(-6).map((message) => message.content).join("\n"),
    };
    const target = kind === "direct" ? participants[0]! : undefined;
    if (target) {
      Object.assign(vars, {
        npc_name: target.identity.name,
        npc_gender: target.identity.pronouns,
        npc_realm: target.hunter.hr ? `HR ${target.hunter.hr}` : target.identity.occupation,
        npc_personality: target.bio.background,
        npc_status: target.status.current,
        npc_action: target.bio.shortTermGoal,
        npc_life_story: target.bio.background,
        npc_deeds: target.bio.recentEvents.join("；"),
        npc_biography: target.bio.biography,
        npc_motive: target.bio.currentMotive,
        favorability: String(affinityTowardsPlayer(target)),
        relationship: target.relations.find((relation) => relation.targetId === "B1")?.label ?? "尚未确立",
        scene_chat_history: renderHistory(conversation),
        chat_mode_description: "现场面对面交流；不存在远程通信。",
      });
    }
    const sourcePrompt = kind === "group" ? preset.groupSystemPrompt : preset.systemPrompt;
    const systemPrompt = [
      MH_IMPORTED_PRESET_ADAPTATION,
      resolvePresetContent(sourcePrompt, vars),
      kind === "group"
        ? '只输出 JSON：{"speakerId":"C1或null","waitForPlayer":false,"narrate":"","speak":"","triggerCombat":false}。think 字段可省略且不会展示或保存。'
        : '只输出 JSON：{"narrate":"","speak":"","triggerCombat":false}。think 字段可省略且不会展示或保存。',
    ].join("\n\n");
    const messages = [
      { role: "system" as const, content: systemPrompt },
      { role: "user" as const, content: lastMessage },
      ...(preset.assistantPrefill?.trim()
        ? [{ role: "assistant" as const, content: preset.assistantPrefill.trim() }]
        : []),
    ];
    const response = await callLLMWithRoutes(
      resolveFeatureRoutes(kind === "group" ? "quick-chat-group" : "quick-chat", options.settings),
      messages,
      {
        signal: options.signal,
        stream: false,
        responseFormat: "json",
        debug: {
          featureId: kind === "group" ? "quick-chat-group" : "quick-chat",
          archiveName: next.name,
        },
      },
    );
    const parsed = kind === "group"
      ? groupSchema.parse(parseJsonObject(response.text))
      : directSchema.parse(parseJsonObject(response.text));
    const speakerId = kind === "direct" ? participants[0]!.id : (parsed as z.infer<typeof groupSchema>).speakerId;
    if (!speakerId || (kind === "group" && (parsed as z.infer<typeof groupSchema>).waitForPlayer)) break;
    const speaker = available.get(speakerId);
    if (!speaker || speakerId === lastSpeaker) break;
    const content = visibleReply(parsed.narrate, parsed.speak);
    if (!content) break;
    appendMessage(conversation, {
      id: crypto.randomUUID(),
      role: "assistant",
      content,
      createdAt: Date.now(),
      speakerId,
    });
    lastMessage = content;
    lastSpeaker = speakerId;
  }
  next.quickChat.conversations[id] = conversation;
  if (!next.quickChat.pendingConversationIds.includes(id))
    next.quickChat.pendingConversationIds.push(id);
  return { archive: next, conversation };
}

export function buildPendingQuickChatContext(archive: Archive) {
  const blocks = archive.quickChat.pendingConversationIds.flatMap((id) => {
    const conversation = archive.quickChat.conversations[id];
    if (!conversation) return [];
    const names = conversation.participantIds
      .map((participantId) => archive.characters[participantId]?.identity.name ?? participantId)
      .join("、");
    return [`【本轮前发生的现场快聊：${names}】\n${renderHistory(conversation)}`];
  });
  return blocks.join("\n\n");
}
